/**
 * Web Chat Server
 * Local HTTP server for web-based chat interface
 * Serves static files and proxies requests to LLM Proxy API
 */
import http from 'http';
import fs from 'fs';
import path from 'path';
import { fileURLToPath } from 'url';
import { SessionManager, Session } from './session.js';
import { LlmProxyService } from '../services/llm-proxy.service.js';
import { FileService } from './file-service.js';
import type { Jai1Config } from '../types/framework.types.js';

const __filename = fileURLToPath(import.meta.url);
const __dirname = path.dirname(__filename);

// MIME types for static files
const MIME_TYPES: Record<string, string> = {
    '.html': 'text/html',
    '.css': 'text/css',
    '.js': 'application/javascript',
    '.json': 'application/json',
    '.png': 'image/png',
    '.svg': 'image/svg+xml',
    '.ico': 'image/x-icon',
};

export interface WebChatServerConfig {
    port?: number;
    host?: string;
    config: Jai1Config;
}

export interface WebChatServer {
    start(): Promise<{ port: number; sessionToken: string }>;
    stop(): Promise<void>;
    getUrl(): string;
}

/**
 * Find an available port in range
 */
async function findAvailablePort(startPort: number, endPort: number): Promise<number> {
    for (let port = startPort; port <= endPort; port++) {
        try {
            await new Promise<void>((resolve, reject) => {
                const server = http.createServer();
                server.listen(port, '127.0.0.1');
                server.once('listening', () => {
                    server.close(() => resolve());
                });
                server.once('error', reject);
            });
            return port;
        } catch {
            continue;
        }
    }
    throw new Error(`No available port found in range ${startPort}-${endPort}`);
}

/**
 * Parse JSON body from request
 */
async function parseJsonBody(req: http.IncomingMessage): Promise<unknown> {
    return new Promise((resolve, reject) => {
        let body = '';
        req.on('data', (chunk) => {
            body += chunk.toString();
        });
        req.on('end', () => {
            try {
                resolve(body ? JSON.parse(body) : {});
            } catch (e) {
                reject(new Error('Invalid JSON body'));
            }
        });
        req.on('error', reject);
    });
}

/**
 * Send JSON response
 */
function sendJson(
    res: http.ServerResponse,
    statusCode: number,
    data: unknown
): void {
    res.writeHead(statusCode, {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'GET, POST, OPTIONS',
        'Access-Control-Allow-Headers': 'Content-Type, X-Session-Token',
    });
    res.end(JSON.stringify(data));
}

/**
 * Send error response
 */
function sendError(
    res: http.ServerResponse,
    statusCode: number,
    code: string,
    message: string
): void {
    sendJson(res, statusCode, {
        success: false,
        error: { code, message },
    });
}

/**
 * Get session token from request
 */
function getSessionToken(req: http.IncomingMessage): string | null {
    // Try header first
    const headerToken = req.headers['x-session-token'];
    if (headerToken && typeof headerToken === 'string') {
        return headerToken;
    }

    // Try query param
    const url = new URL(req.url || '/', `http://${req.headers.host}`);
    return url.searchParams.get('session');
}

/**
 * Create the web chat server
 */
export function createWebChatServer(options: WebChatServerConfig): WebChatServer {
    const { config } = options;
    const host = options.host ?? '127.0.0.1';
    let port = options.port ?? 0;

    const sessionManager = new SessionManager();
    let server: http.Server | null = null;
    let session: Session | null = null;

    // File service for file mention feature
    // Uses the directory where CLI was invoked
    const fileService = new FileService({ workingDir: process.cwd() });

    // Path to static files
    // After build with tsup, __dirname is the dist/ folder
    // Web-chat files are copied to dist/web-chat/ by the build script
    const staticDir = path.join(__dirname, 'web-chat');

    /**
     * Serve static file
     */
    async function serveStaticFile(
        res: http.ServerResponse,
        filePath: string
    ): Promise<void> {
        const fullPath = path.join(staticDir, filePath);

        // Security: prevent path traversal
        if (!fullPath.startsWith(staticDir)) {
            sendError(res, 403, 'ERR-WC-006', 'Forbidden');
            return;
        }

        try {
            const stat = await fs.promises.stat(fullPath);
            if (!stat.isFile()) {
                sendError(res, 404, 'ERR-WC-007', 'Not found');
                return;
            }

            const ext = path.extname(fullPath);
            const contentType = MIME_TYPES[ext] || 'application/octet-stream';

            const content = await fs.promises.readFile(fullPath);
            res.writeHead(200, { 'Content-Type': contentType });
            res.end(content);
        } catch (error: unknown) {
            if (error && typeof error === 'object' && 'code' in error && error.code === 'ENOENT') {
                sendError(res, 404, 'ERR-WC-007', 'Not found');
            } else {
                sendError(res, 500, 'ERR-WC-008', 'Internal server error');
            }
        }
    }

    /**
     * Handle API requests
     */
    async function handleApi(
        req: http.IncomingMessage,
        res: http.ServerResponse,
        pathname: string
    ): Promise<void> {
        // CORS preflight
        if (req.method === 'OPTIONS') {
            res.writeHead(204, {
                'Access-Control-Allow-Origin': '*',
                'Access-Control-Allow-Methods': 'GET, POST, OPTIONS',
                'Access-Control-Allow-Headers': 'Content-Type, X-Session-Token',
            });
            res.end();
            return;
        }

        // Health check (no auth required)
        if (pathname === '/health') {
            sendJson(res, 200, { status: 'ok' });
            return;
        }

        // All other API routes require valid session
        const token = getSessionToken(req);
        const validSession = token ? sessionManager.validateSession(token) : null;

        if (!validSession) {
            sendError(
                res,
                401,
                'ERR-WC-001',
                "Session expired. Please run 'jai1 chat --web' again."
            );
            return;
        }

        // Create LLM service with session credentials
        const llmService = new LlmProxyService({
            accessKey: validSession.accessKey,
            apiUrl: validSession.apiUrl,
        });

        // Route handlers
        if (pathname === '/api/session' && req.method === 'GET') {
            // Get session info
            try {
                const models = await llmService.getModelsWithUsage();
                const allowedModels = models
                    .filter((m) => m.allowed)
                    .map((m) => m.id);

                sendJson(res, 200, {
                    success: true,
                    data: {
                        expiresAt: validSession.expiresAt.toISOString(),
                        models: allowedModels,
                    },
                });
            } catch (error: unknown) {
                sendError(res, 502, 'ERR-WC-004', 'Failed to connect to LLM service.');
            }
            return;
        }

        if (pathname === '/api/models' && req.method === 'GET') {
            // Get available models
            try {
                const models = await llmService.getModelsWithUsage();
                sendJson(res, 200, {
                    success: true,
                    data: models.filter((m) => m.allowed),
                });
            } catch (error: unknown) {
                sendError(res, 502, 'ERR-WC-004', 'Failed to connect to LLM service.');
            }
            return;
        }

        if (pathname === '/api/stats' && req.method === 'GET') {
            // Get model stats (limits and usage)
            try {
                const [limits, usage] = await Promise.all([
                    llmService.getLimits(),
                    llmService.getUsage(1), // Today only
                ]);

                // Use GMT+7 timezone to match API date recording
                const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Ho_Chi_Minh' });

                // Build stats per model
                const modelStats: Record<string, { limit: number; used: number; remaining: number }> = {};
                const allowedModels = limits.effectiveAllowedModels || [];
                const rateLimits = limits.effectiveRateLimits || {};

                allowedModels.forEach((modelId: string) => {
                    const limit = rateLimits[modelId] || rateLimits[modelId.toLowerCase()] || 0;
                    const usageRecord = (usage.data || []).find(
                        (u) => (u.model === modelId || u.model.toLowerCase() === modelId.toLowerCase()) && u.date === today
                    );
                    const used = usageRecord?.count || 0;

                    modelStats[modelId] = {
                        limit,
                        used,
                        remaining: Math.max(0, limit - used),
                    };
                });

                sendJson(res, 200, {
                    success: true,
                    data: {
                        date: today,
                        models: modelStats,
                    },
                });
            } catch (error: unknown) {
                sendError(res, 502, 'ERR-WC-004', 'Failed to connect to LLM service.');
            }
            return;
        }

        // === File API Endpoints ===

        if (pathname === '/api/files/search' && req.method === 'GET') {
            // Search files for autocomplete
            try {
                const url = new URL(req.url || '/', `http://${req.headers.host}`);
                const query = url.searchParams.get('q') || '';
                const limit = parseInt(url.searchParams.get('limit') || '10', 10);

                if (!query || query.length < 1) {
                    sendJson(res, 200, { success: true, data: { files: [] } });
                    return;
                }

                const files = await fileService.searchFiles(query, Math.min(limit, 20));
                sendJson(res, 200, {
                    success: true,
                    data: { files },
                });
            } catch (error: unknown) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                sendError(res, 500, 'ERR-WC-011', `File search failed: ${errorMessage}`);
            }
            return;
        }

        if (pathname === '/api/files/read' && req.method === 'GET') {
            // Read single file
            try {
                const url = new URL(req.url || '/', `http://${req.headers.host}`);
                const filePath = url.searchParams.get('path');

                if (!filePath) {
                    sendError(res, 400, 'ERR-WC-012', 'Missing file path');
                    return;
                }

                const fileContent = await fileService.readFile(filePath);
                sendJson(res, 200, {
                    success: true,
                    data: fileContent,
                });
            } catch (error: unknown) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                if (errorMessage.includes('Path traversal')) {
                    sendError(res, 403, 'ERR-WC-013', 'Access denied');
                } else if (errorMessage.includes('ENOENT')) {
                    sendError(res, 404, 'ERR-WC-014', 'File not found');
                } else {
                    sendError(res, 400, 'ERR-WC-015', errorMessage);
                }
            }
            return;
        }

        if (pathname === '/api/files/batch' && req.method === 'POST') {
            // Read multiple files
            try {
                const body = (await parseJsonBody(req)) as { paths?: string[] };

                if (!body.paths || !Array.isArray(body.paths) || body.paths.length === 0) {
                    sendError(res, 400, 'ERR-WC-016', 'Missing or invalid paths array');
                    return;
                }

                if (body.paths.length > fileService.getMaxFilesLimit()) {
                    sendError(res, 400, 'ERR-WC-017', `Maximum ${fileService.getMaxFilesLimit()} files allowed per request`);
                    return;
                }

                const files = await fileService.readFiles(body.paths);
                sendJson(res, 200, {
                    success: true,
                    data: { files },
                });
            } catch (error: unknown) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                sendError(res, 500, 'ERR-WC-018', `Batch read failed: ${errorMessage}`);
            }
            return;
        }

        if (pathname === '/api/files/info' && req.method === 'GET') {
            // Get file service info
            sendJson(res, 200, {
                success: true,
                data: {
                    workingDir: fileService.getWorkingDir(),
                    maxFiles: fileService.getMaxFilesLimit(),
                },
            });
            return;
        }

        if (pathname === '/api/chat' && req.method === 'POST') {
            // Chat endpoint with streaming
            try {
                const body = (await parseJsonBody(req)) as {
                    message: string;
                    model: string;
                    history?: Array<{ role: string; content: string }>;
                    systemPrompt?: string | null;
                };

                if (!body.message || !body.model) {
                    sendError(res, 400, 'ERR-WC-009', 'Missing message or model');
                    return;
                }

                // Build messages array with optional system prompt
                const messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }> = [];

                // Add system prompt if provided
                if (body.systemPrompt) {
                    messages.push({ role: 'system', content: body.systemPrompt });
                }

                // Add history and current message
                messages.push(
                    ...(body.history || []).map((m) => ({
                        role: m.role as 'user' | 'assistant',
                        content: m.content,
                    })),
                    { role: 'user' as const, content: body.message }
                );

                // Set up SSE
                res.writeHead(200, {
                    'Content-Type': 'text/event-stream',
                    'Cache-Control': 'no-cache',
                    Connection: 'keep-alive',
                    'Access-Control-Allow-Origin': '*',
                });

                try {
                    // Stream response
                    for await (const chunk of llmService.chatStream(
                        messages,
                        body.model
                    )) {
                        res.write(
                            `data: ${JSON.stringify({ type: 'chunk', content: chunk })}\n\n`
                        );
                    }

                    res.write(`data: ${JSON.stringify({ type: 'done' })}\n\n`);
                } catch (streamError: unknown) {
                    const errorMessage =
                        streamError instanceof Error
                            ? streamError.message
                            : 'Unknown error';

                    // Check for specific error types
                    if (errorMessage.includes('not allowed')) {
                        res.write(
                            `data: ${JSON.stringify({ type: 'error', code: 'ERR-WC-002', message: "You don't have access to this model." })}\n\n`
                        );
                    } else if (errorMessage.includes('rate limit')) {
                        res.write(
                            `data: ${JSON.stringify({ type: 'error', code: 'ERR-WC-003', message: 'Rate limit exceeded. Please wait.' })}\n\n`
                        );
                    } else {
                        res.write(
                            `data: ${JSON.stringify({ type: 'error', code: 'ERR-WC-004', message: errorMessage })}\n\n`
                        );
                    }
                }

                res.end();
            } catch (error: unknown) {
                sendError(res, 500, 'ERR-WC-008', 'Internal server error');
            }
            return;
        }

        // Unknown API endpoint
        sendError(res, 404, 'ERR-WC-010', 'API endpoint not found');
    }

    /**
     * Main request handler
     */
    async function handleRequest(
        req: http.IncomingMessage,
        res: http.ServerResponse
    ): Promise<void> {
        const url = new URL(req.url || '/', `http://${req.headers.host}`);
        const pathname = url.pathname;

        try {
            // API routes
            if (pathname.startsWith('/api/') || pathname === '/health') {
                await handleApi(req, res, pathname);
                return;
            }

            // Static files
            let filePath = pathname === '/' ? '/index.html' : pathname;

            // Remove leading slash for path.join
            filePath = filePath.replace(/^\//, '');

            await serveStaticFile(res, filePath);
        } catch (error) {
            console.error('[WebChatServer] Error:', error);
            sendError(res, 500, 'ERR-WC-008', 'Internal server error');
        }
    }

    return {
        async start(): Promise<{ port: number; sessionToken: string }> {
            // Find available port if not specified
            if (!port || port === 0) {
                port = await findAvailablePort(54321, 54399);
            }

            // Create session
            session = sessionManager.createSession({
                accessKey: config.accessKey,
                apiUrl: config.apiUrl,
            });

            // Create HTTP server
            server = http.createServer(handleRequest);

            return new Promise((resolve, reject) => {
                server!.listen(port, host, () => {
                    console.log(`\n🌐 Web Chat server started at http://${host}:${port}`);
                    console.log(`📋 Session token: ${session!.token.slice(0, 12)}...`);
                    console.log(`⏰ Session expires in 30 minutes\n`);

                    resolve({
                        port,
                        sessionToken: session!.token,
                    });
                });

                server!.once('error', (err: NodeJS.ErrnoException) => {
                    if (err.code === 'EADDRINUSE') {
                        reject(new Error(`Port ${port} is already in use.`));
                    } else {
                        reject(err);
                    }
                });
            });
        },

        async stop(): Promise<void> {
            if (server) {
                return new Promise((resolve) => {
                    server!.close(() => {
                        sessionManager.clearAll();
                        console.log('\n👋 Web Chat server stopped');
                        resolve();
                    });
                });
            }
        },

        getUrl(): string {
            return `http://${host}:${port}/?session=${session?.token || ''}`;
        },
    };
}
